import { useState, useEffect, useCallback } from 'react'
import { useAccount } from 'wagmi'
import { BuyBackItem } from './types'
import {
  getBuyBackHistory,
  removeBuyBackItem,
  clearBuyBackHistory,
  formatUSDAmount,
} from './buybackUtils'
import {trackRevenue, trackSwapBuybackWithRefcode} from '../services/analyticsService'
import { getRefCode } from '../utils/localStorage'
import { AccountController } from '../controller/AccountController'
import { WalletType } from '../types/wallet'

const useBuyBack = (userAddress?: string, walletType: WalletType = 'EVM') => {
  const { address: wagmiAddress } = useAccount()
  // const chainId = useChainId()
  // const config = useConfig()
  // const { switchChainAsync } = useSwitchChain()
  // const walletService = useWalletService()

  const [buybackHistory, setBuybackHistory] = useState<BuyBackItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isBuyingBack, setIsBuyingBack] = useState(false)
  // UI-only status tracking (not persisted)
  const [itemStatuses, setItemStatuses] = useState<
    Record<string, 'pending' | 'processing' | 'completed' | 'failed'>
  >({})

  // Load buyback history
  useEffect(() => {
    if (userAddress) {
      const history = getBuyBackHistory(userAddress)
      if (history) {
        setBuybackHistory(history.items)
      }
    } else {
      setBuybackHistory([])
    }
  }, [userAddress])

  // All items are considered available for buyback (status is UI-only)
  const pendingItems = buybackHistory.filter((item) => {
    const status = itemStatuses[item.id] || 'pending'
    return status === 'pending' || status === 'failed'
  })
  const totalPendingUSD = buybackHistory.reduce((sum, item) => sum + item.buybackAmountUSD, 0)

  /**
   * Execute buyback for a single item
   * This reverses the swap: from outputToken back to inputToken
   */
  const buyBackItem = useCallback(
    async (item: BuyBackItem): Promise<boolean> => {
      setItemStatuses((prev) => ({ ...prev, [item.id]: 'processing' }))
      if (!userAddress || (walletType === 'EVM' && !wagmiAddress)) {
        return false
      }

      await AccountController.self().buyBackItem(
        item,
        (swapHash) => {
          setBuybackHistory((prev) =>
            prev.map((i) => (i.id === item.id ? { ...i, txHash: swapHash } : i)),
          )
          // Update UI status only
          setItemStatuses((prev) => ({ ...prev, [item.id]: 'completed' }))

          const refCode = getRefCode()
          trackRevenue(swapHash, refCode, item.buybackAmountUSD, 'sell', AccountController.self().type)

          // Reset to pending after 3 seconds so user can buyback again if needed
          setTimeout(() => {
            setItemStatuses((prev) => ({ ...prev, [item.id]: 'pending' }))
          }, 3000)
        },
        () => {
          setItemStatuses((prev) => ({ ...prev, [item.id]: 'failed' }))

          // Reset to pending after 5 seconds so user can retry
          setTimeout(() => {
            setItemStatuses((prev) => ({ ...prev, [item.id]: 'pending' }))
          }, 5000)
        },
      )
      setIsBuyingBack(false)
      setIsLoading(false)
      return
      // Set processing status
      // try {
      //   setIsBuyingBack(true)
      //   setIsLoading(true)
      //
      //   // Find network config
      //   const network = SupportedNetWork.find((n: any) => n.chainId === item.inputToken.chainId)
      //   if (!network) {
      //     throw new Error(`Network ${item.inputToken.chainId} not found`)
      //   }
      //
      //   // Setup ParaSwap controller for this network
      //   if (
      //     network.chainId !== paraController.network ||
      //     ![network.fallBack, network.rpc].includes(
      //       (paraController.web3.currentProvider as HttpProvider)?.host,
      //     )
      //   ) {
      //     paraController.onChangeNetwork(network)
      //   }
      //
      //   // Get tokens from ParaSwap
      //   // For buyback: outputToken (from sell) becomes sourceToken, inputToken becomes destToken
      //   const sourceTokenForBuyback: LiquidationAsset = {
      //     symbol: item.outputToken.symbol,
      //     name: item.outputToken.name,
      //     address: item.outputToken.address,
      //     decimals: item.outputToken.decimals,
      //     network: network as NetworkConfig,
      //     isNative: item.outputToken.isNative,
      //     balance: '0',
      //     balanceString: '0',
      //     USDBalance: item.receivedAmountUSD,
      //     stringUSDBalance: item.receivedAmountUSD.toString(),
      //     contract_address: item.outputToken.address,
      //     cmcCoinId: 0,
      //     isSelected: true,
      //     loading: false,
      //     allocation: 0,
      //     price: '',
      //     error: '',
      //     nativeCurrency: network.nativeCurrency || {
      //       name: network.symbol,
      //       symbol: network.symbol,
      //       cmcCoinId: 0,
      //     },
      //     stringBalance: '0',
      //   }
      //
      //   const destTokenForBuyback: LiquidationAsset = {
      //     symbol: item.inputToken.symbol,
      //     name: item.inputToken.name,
      //     address: item.inputToken.address,
      //     decimals: item.inputToken.decimals,
      //     network: network as NetworkConfig,
      //     isNative: item.inputToken.isNative,
      //     balance: '0',
      //     balanceString: '0',
      //     USDBalance: item.soldAmountUSD,
      //     stringUSDBalance: item.soldAmountUSD.toString(),
      //     contract_address: item.inputToken.address,
      //     cmcCoinId: 0,
      //     isSelected: true,
      //     loading: false,
      //     allocation: 0,
      //     price: '',
      //     error: '',
      //     nativeCurrency: network.nativeCurrency || {
      //       name: network.symbol,
      //       symbol: network.symbol,
      //       cmcCoinId: 0,
      //     },
      //     stringBalance: '0',
      //   }
      //
      //   // For buyback, we need to determine target coin type
      //   // Check if outputToken is USDC, USDT, or Native
      //   let targetCoinType: TargetCoinLiquidation = TargetCoinLiquidation.USDC
      //   if (item.outputToken.symbol === 'USDT') {
      //     targetCoinType = TargetCoinLiquidation.USDT
      //   } else if (
      //     item.outputToken.isNative ||
      //     item.outputToken.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
      //   ) {
      //     targetCoinType = TargetCoinLiquidation.NATIVE
      //   }
      //
      //   // Get actual token objects from ParaSwap
      //   // For buyback: sourceToken is outputToken (what we received from sell)
      //   const { inputToken: buybackSourceToken } = await getSourceDestTokenByNetwork(
      //     network.chainId,
      //     sourceTokenForBuyback,
      //     targetCoinType,
      //   )
      //
      //   // For buyback: we swap from outputToken (buybackSourceToken) to inputToken (we need to find it)
      //   // First, find the inputToken token from ParaSwap
      //   const tokensOrError = await paraController.paraSwap.getTokens()
      //   let buybackDestTokenObj: any = null
      //
      //   if (Array.isArray(tokensOrError)) {
      //     buybackDestTokenObj = tokensOrError.find(
      //       (token: any) =>
      //         token.symbol?.toLowerCase() === item.inputToken.symbol.toLowerCase() &&
      //         token.address?.toLowerCase() === item.inputToken.address.toLowerCase(),
      //     )
      //   }
      //
      //   if (!buybackDestTokenObj) {
      //     throw new Error(`Could not find ${item.inputToken.symbol} token on ${network.name}`)
      //   }
      //
      //   // Get current balance of outputToken first
      //   const { balance } = await getTokenBalance({
      //     userAddress: userAddress,
      //     token: buybackSourceToken,
      //     spender: '',
      //   })
      //
      //   const balanceFormatted = formatBigNumberToAmount(
      //     balance ? balance.toString() : '0',
      //     buybackSourceToken.decimals,
      //   )
      //
      //   // Calculate amount needed: convert buybackAmountUSD to token amount
      //   // We'll use a reasonable estimate based on the USD value we want to spend
      //   // Try to estimate amount by using receivedAmountUSD as reference
      //   // If we received X tokens for Y USD, then Y USD should get us approximately X tokens back
      //   const estimatedTokenAmount =
      //     item.buybackAmountUSD / (item.receivedAmountUSD / Number(item.receivedAmount))
      //
      //   // Determine actual amount to spend:
      //   // - If have < need: use full balance (have)
      //   // - If have >= need: use estimated amount (need)
      //   const actualTokenAmount =
      //     Number(balanceFormatted) < estimatedTokenAmount
      //       ? Number(balanceFormatted) // Use full balance if insufficient
      //       : estimatedTokenAmount // Use estimated amount if sufficient
      //
      //   // Check if we have any balance at all
      //   if (actualTokenAmount <= 0 || Number(balanceFormatted) <= 0) {
      //     throw new Error(
      //       `Insufficient ${item.outputToken.symbol} balance for buyback. Have ${Number(balanceFormatted).toFixed(6)} ${item.outputToken.symbol}`,
      //     )
      //   }
      //
      //   // Convert actual amount to raw format (multiply by 10^decimals)
      //   // This is why we use parseUnits - blockchain needs raw integer amount, not human-readable decimal
      //   // Example: 1 USDC (decimals=18) = 1000000000000000000 (10^18) units
      //   // const sourceAmountNeeded = ethers.utils.parseUnits(
      //   //   actualTokenAmount.toFixed(buybackSourceToken.decimals),
      //   //   buybackSourceToken.decimals,
      //   // ).toString()
      //
      //   // Get final price route with calculated amount
      //   const { priceRoute, bnbCost } = await getBestPrice({
      //     amount: actualTokenAmount.toString(),
      //     inputToken: buybackSourceToken as any,
      //     outputToken: {
      //       ...destTokenForBuyback,
      //       address: buybackDestTokenObj.address,
      //       decimals: buybackDestTokenObj.decimals,
      //     } as any,
      //     userAddress: userAddress,
      //     onHandleError: () => {},
      //   })
      //
      //   if (!priceRoute) {
      //     throw new Error('Failed to get buyback price route')
      //   }
      //
      //   // Check if we have enough balance including gas
      //   const nativeBalance = await getNativeBalance(userAddress)
      //   if (nativeBalance < Number(bnbCost)) {
      //     throw new Error(
      //       `Insufficient native token for gas. Need ${bnbCost}, have ${nativeBalance}`,
      //     )
      //   }
      //
      //   // Switch chain if needed
      //   if (chainId !== network.chainId) {
      //     try {
      //       await switchChainAsync({ chainId: network.chainId })
      //       await new Promise((resolve) => setTimeout(resolve, 2000))
      //     } catch (switchError) {
      //       throw new Error(`Failed to switch to ${network.name}`)
      //     }
      //   }
      //
      //   // Check allowance for ERC20 tokens
      //   if (!item.outputToken.isNative) {
      //     const { allowance } = await getTokenBalance({
      //       userAddress: userAddress,
      //       token: buybackSourceToken,
      //       spender: priceRoute.tokenTransferProxy,
      //     })
      //
      //     const isAllowanceSufficient =
      //       allowance &&
      //       allowance !== '0' &&
      //       ethers.BigNumber.from(allowance).gte(ethers.BigNumber.from(priceRoute.srcAmount))
      //
      //     if (!isAllowanceSufficient) {
      //       // Need to approve first
      //       if (
      //         !priceRoute.tokenTransferProxy ||
      //         priceRoute.tokenTransferProxy === '0x0000000000000000000000000000000000000000'
      //       ) {
      //         throw new Error('Invalid spender address')
      //       }
      //
      //       // Use wallet service for approval
      //       if (!walletService) {
      //         throw new Error('Wallet service not available')
      //       }
      //
      //       const chain =
      //         config.chains.find((chain) => chain.id === network.chainId) || config.chains[0]
      //
      //       const approvalHash = await walletService.approveToken({
      //         tokenAddress: buybackSourceToken.address as Address,
      //         spenderAddress: priceRoute.tokenTransferProxy as Address,
      //         amount: BigInt(priceRoute.srcAmount),
      //         account: wagmiAddress as Address,
      //         chain,
      //       })
      //
      //       // Wait for approval transaction confirmation
      //       await new Promise((resolve) => setTimeout(resolve, 10000))
      //     }
      //   }
      //
      //   // Build swap transaction
      //   const txParams = await paraController.buildSwap(
      //     buybackSourceToken as any,
      //     {
      //       ...destTokenForBuyback,
      //       address: buybackDestTokenObj.address,
      //       decimals: buybackDestTokenObj.decimals,
      //     } as any,
      //     priceRoute.srcAmount,
      //     priceRoute.destAmount,
      //     userAddress as Address,
      //   )
      //
      //   // Execute swap using wallet service
      //   if (!walletService) {
      //     throw new Error('Wallet service not available')
      //   }
      //
      //   const chain =
      //     config.chains.find((chain) => chain.id === network.chainId) || config.chains[0]
      //
      //   const swapHash = await walletService.sendTransaction({
      //     account: wagmiAddress as Address,
      //     to: txParams.to as Address,
      //     value: BigInt(txParams.value || '0'),
      //     data: txParams.data as `0x${string}`,
      //     gas: txParams.gas ? BigInt(txParams.gas) : undefined,
      //     chain,
      //   })
      //
      //   // Update txHash in storage (status is UI-only)
      //   updateBuyBackItemTxHash(userAddress, item.id, swapHash)
      //   setBuybackHistory((prev) =>
      //     prev.map((i) => (i.id === item.id ? { ...i, txHash: swapHash } : i)),
      //   )
      //
      //   // Update UI status only
      //   setItemStatuses((prev) => ({ ...prev, [item.id]: 'completed' }))
      //
      //   // Reset to pending after 3 seconds so user can buyback again if needed
      //   setTimeout(() => {
      //     setItemStatuses((prev) => ({ ...prev, [item.id]: 'pending' }))
      //   }, 3000)
      //
      //   return true
      // } catch (error: any) {
      //   // Update UI status only
      //   setItemStatuses((prev) => ({ ...prev, [item.id]: 'failed' }))
      //
      //   // Reset to pending after 5 seconds so user can retry
      //   setTimeout(() => {
      //     setItemStatuses((prev) => ({ ...prev, [item.id]: 'pending' }))
      //   }, 5000)
      //
      //   throw error
      // } finally {
      //   setIsBuyingBack(false)
      //   setIsLoading(false)
      // }
    },
    [userAddress, walletType, wagmiAddress],
  )

  /**
   * Buyback all pending items
   */
  const buyBackAll = useCallback(async (): Promise<void> => {
    const pending = pendingItems
    if (pending.length === 0) return

    // Calculate total buyback amount before executing
    const totalBuybackAmount = pending.reduce((sum, item) => sum + item.buybackAmountUSD, 0)

    for (const item of pending) {
      try {
        await buyBackItem(item)
        // Small delay between transactions
        await new Promise((resolve) => setTimeout(resolve, 3000))
      } catch (error) {
        // Continue with next item
      }
    }

    // Track total buyback swap once after all items are processed
    const refCode = getRefCode()
    if (refCode && totalBuybackAmount > 0) {
      trackSwapBuybackWithRefcode(
        refCode,
        totalBuybackAmount,
        walletType === 'SOL' ? 'sol' : walletType === 'SUI' ? 'sui' : 'evm',
      )
    }
  }, [pendingItems, buyBackItem, walletType])

  /**
   * Remove item from history
   */
  const removeItem = useCallback(
    (itemId: string) => {
      if (!userAddress) return
      removeBuyBackItem(userAddress, itemId)
      setBuybackHistory((prev) => prev.filter((item) => item.id !== itemId))
    },
    [userAddress],
  )

  /**
   * Clear all history
   */
  const clearHistory = useCallback(() => {
    if (!userAddress) return
    clearBuyBackHistory(userAddress)
    setBuybackHistory([])
  }, [userAddress])

  return {
    buybackHistory,
    pendingItems,
    totalPendingUSD,
    isLoading,
    isBuyingBack,
    buyBackItem,
    buyBackAll,
    removeItem,
    clearHistory,
    formatUSDAmount,
    itemStatuses,
  }
}

export default useBuyBack
